import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import { AccountMcpClient, PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';

type StarterClient = Exclude<AccountMcpClient, 'whatsapp'>;
type PromptKind = 'setup' | 'review';

const CopyPromptButton = ({
  client,
  promptKind,
}: {
  client: StarterClient;
  promptKind: PromptKind;
}) => {
  const { t } = useTranslation(['account_mcp', 'common', 'integrations']);
  const { capture } = usePostHogAnalytics();
  const { copied, markCopied } = useCopiedFeedback();

  return (
    <Button
      variant="dark"
      size="sm"
      disabled={copied}
      data-testid={`account-mcp-${client}-copy-prompt`}
      onClick={() =>
        copyToClipboard(
          t(`first_task.${promptKind}_prompt`),
          () => {
            markCopied();
            capture(PostHogEventEnum.AccountMcpActionClicked, {
              client,
              action: 'copy_prompt',
              prompt_kind: promptKind,
            });
          },
          () => toast.error(t('integrations:copy_failed')),
        )
      }
    >
      {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
      {copied ? t('common:copied') : t('first_task.copy')}
    </Button>
  );
};

export const AccountMcpStarter = ({ client }: { client: StarterClient }) => {
  const { t } = useTranslation('account_mcp');
  const [promptKind, setPromptKind] = useState<PromptKind>('setup');

  return (
    <Card data-testid={`account-mcp-${client}-first-task`}>
      <CardHeader className="p-4">
        <CardTitle>{t('first_task.title')}</CardTitle>
        <CardDescription>
          {t('first_task.description', { client: t(`tab.${client}`) })}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <p className="text-sm leading-relaxed" data-testid={`account-mcp-${client}-prompt`}>
          {t(`first_task.${promptKind}_prompt`)}
        </p>
      </CardContent>
      <CardFooter className="flex-wrap gap-2 px-4 pb-4">
        <CopyPromptButton key={promptKind} client={client} promptKind={promptKind} />
        <Button
          variant="link"
          size="sm"
          className="h-auto whitespace-normal text-left"
          data-testid={`account-mcp-${client}-switch-prompt`}
          onClick={() => setPromptKind(promptKind === 'setup' ? 'review' : 'setup')}
        >
          {t(promptKind === 'setup' ? 'first_task.switch_to_review' : 'first_task.switch_to_setup')}
        </Button>
      </CardFooter>
    </Card>
  );
};
